/**
 * ErrorBoundary
 *
 * Rede de segurança para erros de renderização do React. Sem isto, QUALQUER
 * exceção não tratada durante o render desmonta a árvore inteira e, como o tema
 * é quase preto (--bg-primary: #0a0a0a), o usuário vê a TELA PRETA e não
 * consegue mais clicar em nada (nem voltar, nem criar nota/tarefa, nem mover o
 * lead). Foi exatamente o sintoma relatado ao abrir os "Dados do contato".
 *
 * Uso:
 *   1) Global (App.js): envolve a app toda — uma falha em qualquer tela vira um
 *      cartão amigável com "Recarregar", em vez do vazio preto.
 *   2) Local (ex.: em volta do ContactDetailsDrawer): com `fallback` próprio, um
 *      crash do drawer NÃO derruba o ChatWindow — o vendedor continua no chat.
 *
 * Props:
 *   - children
 *   - fallback   -> node | ({ error, reset }) => node   (opcional)
 *   - resetKeys  -> array; quando muda, limpa o estado de erro (ex.: fechar e
 *                   reabrir o drawer, ou trocar de contato) p/ não travar pra sempre
 *   - onError(error, info)  -> callback opcional
 *   - name       -> rótulo p/ o log
 */
import React from 'react';

// Cores fixas (não dependem do ThemeContext, pois o boundary global fica ACIMA
// do ThemeProvider e precisa renderizar mesmo se o tema falhar). Alinhadas com a
// LoadingScreen do App.js para manter a identidade visual.
const FB = {
  bg: '#111b21',
  card: '#1f2c33',
  border: '#2a3942',
  text: '#e9edef',
  textDim: '#8696a0',
  accent: '#00a884',
};

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // Log com o stack de componentes — essencial para achar a origem em produção.
    // eslint-disable-next-line no-console
    console.error(
      `[ErrorBoundary:${this.props.name || 'app'}]`,
      error,
      info && info.componentStack
    );
    if (typeof this.props.onError === 'function') {
      try { this.props.onError(error, info); } catch (_) { /* nunca relança aqui */ }
    }
  }

  componentDidUpdate(prevProps) {
    if (!this.state.error) return;
    const a = prevProps.resetKeys || [];
    const b = this.props.resetKeys || [];
    const changed = a.length !== b.length || a.some((v, i) => !Object.is(v, b[i]));
    if (changed) {
      // Limpa o erro para remontar os filhos (nova tentativa) quando o contexto muda.
      this.setState({ error: null });
    }
  }

  reset = () => this.setState({ error: null });

  render() {
    const { error } = this.state;
    if (error) {
      const { fallback } = this.props;
      if (typeof fallback === 'function') {
        return fallback({ error, reset: this.reset });
      }
      if (fallback !== undefined) {
        return fallback;
      }
      return <DefaultFallback error={error} />;
    }
    return this.props.children;
  }
}

const DefaultFallback = ({ error }) => (
  <div
    role="alert"
    style={{
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: 24,
      backgroundColor: FB.bg,
      color: FB.text,
      fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
    }}
  >
    <div
      style={{
        maxWidth: 440,
        width: '100%',
        backgroundColor: FB.card,
        border: `1px solid ${FB.border}`,
        borderRadius: 16,
        padding: '28px 24px',
        textAlign: 'center',
        boxShadow: '0 20px 60px rgba(0,0,0,0.35)',
      }}
    >
      <h1 style={{ margin: '0 0 8px', fontSize: 18, fontWeight: 600 }}>
        Algo deu errado
      </h1>
      <p style={{ margin: '0 0 20px', fontSize: 14, color: FB.textDim, lineHeight: 1.5 }}>
        Encontramos um erro inesperado nesta tela. Seus dados estão seguros.
        Recarregue a página para continuar.
      </p>
      <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap' }}>
        <button
          type="button"
          onClick={() => window.location.reload()}
          style={{
            padding: '10px 20px',
            backgroundColor: FB.accent,
            color: '#fff',
            border: 'none',
            borderRadius: 10,
            fontSize: 14,
            fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          Recarregar a página
        </button>
        <button
          type="button"
          onClick={() => { window.location.href = '/'; }}
          style={{
            padding: '10px 20px',
            backgroundColor: 'transparent',
            color: FB.text,
            border: `1px solid ${FB.border}`,
            borderRadius: 10,
            fontSize: 14,
            fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          Voltar ao início
        </button>
      </div>
      {process.env.NODE_ENV !== 'production' && error && (
        <pre
          style={{
            marginTop: 20,
            textAlign: 'left',
            fontSize: 11,
            color: '#f87171',
            backgroundColor: '#0b141a',
            borderRadius: 8,
            padding: 12,
            overflow: 'auto',
            maxHeight: 200,
            whiteSpace: 'pre-wrap',
          }}
        >
          {String(error && error.stack ? error.stack : error)}
        </pre>
      )}
    </div>
  </div>
);

/**
 * Fallback no formato do drawer "Dados do contato": painel à direita com aviso
 * e botão Fechar. Usado nos ChatWindows para que um crash do drawer vire um
 * aviso fechável — o chat por trás segue utilizável.
 */
export const DrawerErrorFallback = ({ colors, onClose }) => {
  const c = colors || {};
  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1200,
        display: 'flex', justifyContent: 'flex-end',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 420, maxWidth: '100%', height: '100%',
          backgroundColor: c.bgPrimary || FB.bg,
          boxShadow: '-8px 0 32px rgba(0,0,0,0.25)',
          display: 'flex', flexDirection: 'column',
          padding: 24, gap: 14,
        }}
      >
        <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600, color: c.textPrimary || FB.text }}>
          Dados do contato
        </h3>
        <p style={{ margin: 0, fontSize: 13, lineHeight: 1.5, color: c.textSecondary || FB.textDim }}>
          Não foi possível abrir os dados deste contato agora. Você pode fechar e
          continuar conversando normalmente — nada foi perdido.
        </p>
        <button
          type="button"
          onClick={onClose}
          style={{
            alignSelf: 'flex-start',
            padding: '9px 18px',
            backgroundColor: c.accentPrimary || FB.accent,
            color: '#fff', border: 'none', borderRadius: 10,
            fontSize: 13, fontWeight: 600, cursor: 'pointer',
          }}
        >
          Fechar
        </button>
      </div>
    </div>
  );
};

export default ErrorBoundary;
